import React, { useState, useEffect, useMemo } from 'react';
import {
  BookOpen,
  Search,
  Upload,
  FileText,
  Download,
  Eye,
  X,
  Calendar,
  User,
  Building,
  Trash2,
  Filter,
  CheckCircle2,
  Share2,
  ZoomIn,
  ZoomOut,
  Check,
} from 'lucide-react';
import { Toast, useToast } from '../components/shared/Toast';
import { researchAPI } from '../services/api';

const STORAGE_KEY = 'bciis_research_papers_v2';

const CATEGORIES = [
  'All',
  'Farming & Agronomy',
  'Pest & Disease Control',
  'Processing & Quality',
  'Market & Economy',
  'Soil & Nutrition',
];

const DEFAULT_PAPERS = [
  {
    id: 'res-default-1',
    title: 'Soil Micronutrient and pH Dynamics for High-Yield Cacao Cultivation in Bohol',
    authors: 'Dr. Elena Vance, Engr. Mark Torralba',
    institution: 'Bohol Island State University (BISU) Bilar Campus',
    category: 'Soil & Nutrition',
    date: '2025-11-14',
    fileName: 'Bohol_Cacao_Soil_Micronutrient_Study_2025.pdf',
    fileSize: '2.4 MB',
    fileType: 'application/pdf',
    downloads: 142,
    description:
      'This study evaluates the soil mineral distribution, organic matter content, and pH levels across 14 municipalities in Bohol. Results indicate optimal potassium and nitrogen ratios significantly increase cacao bean yield by 28%. Guidelines for soil management in acidic upland soils are detailed.',
    sections: {
      abstract: 'An in-depth empirical investigation into soil composition and micronutrient supplementation strategies tailored for cacao varieties grown in Bohol upland environments.',
      introduction: 'Cacao (Theobroma cacao L.) cultivation in Bohol requires targeted nutrient management due to regional variations in soil acidity and limestone bedrock formations.',
      methodology: 'Soil samples were collected from 42 test plots across Bilar, Carmen, and Sierra Bullones. Soil samples were analyzed for NPK values, micro-trace elements (Zinc, Boron, Magnesium), and organic carbon content using atomic absorption spectroscopy.',
      keyFindings: [
        'Soil pH levels ranging between 6.2 and 6.8 yielded the highest bean dry weight per pod.',
        'Magnesium deficiencies were correlated with early leaf chlorosis in 35% of observed plots.',
        'Organic compost application at 5kg/tree increased soil moisture retention by 19% during dry spells.'
      ],
      conclusion: 'Implementing balanced fertilization with localized compost integration provides a resilient soil ecosystem for sustained cacao productivity in Bohol.'
    }
  },
  {
    id: 'res-default-2',
    title: 'Integrated Pest Management (IPM) Protocols for Cacao Pod Borer and Black Pod Rot Control',
    authors: 'Dr. Roberto Mendoza, Liza Santos, MSc',
    institution: 'Department of Agriculture - RFO 7 & Makasa Laboratory',
    category: 'Pest & Disease Control',
    date: '2025-09-28',
    fileName: 'Cacao_IPM_Control_Protocols_v3.docx',
    fileSize: '1.8 MB',
    fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    downloads: 98,
    description:
      'Comprehensive field manual detailing bio-fungicide applications, sanitation routines, and sleeve-bagging techniques to combat Conopomorpha cramerella (Cacao Pod Borer) and Phytophthora palmivora in Visayas cacao farms.',
    sections: {
      abstract: 'A operational manual designed for smallholder farmers to mitigate pod infestation without excessive chemical pesticide dependency.',
      introduction: 'Pest infestation remains a primary constraint for cacao growers in Central Visayas, reducing potential farm revenue by up to 40% when left unmanaged.',
      methodology: 'Field trials conducted over 18 months evaluating physical pod bagging (sleeving) combined with biological antagonist Trichoderma harzianum sprays.',
      keyFindings: [
        'Polypropylene pod sleeves reduced pod borer damage to less than 3% across test farms.',
        'Weekly sanitation (harvesting all ripe/infected pods) reduced fungal spore persistence by 62%.',
        'Biological spray application preserved beneficial predator insects including weaver ants.'
      ],
      conclusion: 'Adopting a 4-step IPM routine significantly enhances bean yield while minimizing environmental impact and pesticide residue.'
    }
  },
  {
    id: 'res-default-3',
    title: 'Post-Harvest Fermentation Dynamics and Bean Quality Grading of Bohol Criollo & Trinitario',
    authors: 'Prof. Gabriel Lim, Dr. Carmen Reyes',
    institution: 'Philippine Cacao Industry Association (PCIA)',
    category: 'Processing & Quality',
    date: '2025-06-12',
    fileName: 'Criollo_Beans_Fermentation_Quality_Report.pdf',
    fileSize: '3.1 MB',
    fileType: 'application/pdf',
    downloads: 215,
    description:
      'Investigation into temperature, fermentation box geometry, and turning frequencies required to achieve premium Grade A bean status with high flavanol retention and balanced chocolate aroma profiles.',
    sections: {
      abstract: 'Scientific breakdown of fermentation kinetics using tiered wooden boxes to optimize flavor precursor development in Bohol-harvested cocoa beans.',
      introduction: 'Post-harvest processing dictates bean quality grade in international specialty chocolate markets. Proper microbial succession during fermentation is critical.',
      methodology: 'Beans were fermented in 3-tiered wooden boxes for 5 to 7 days, monitored for temperature shifts, cut-test bean coloration, and sensory flavor notes.',
      keyFindings: [
        'A 6-day fermentation cycle with daily turnings yielded a 92% cut-test pass rate for brown bean interior.',
        'Peak box temperature of 48°C to 50°C on Day 4 ensured optimal breakdown of bitterness compounds.',
        'Sun drying on raised bamboo beds for 6 days preserved volatile aromatic compounds.'
      ],
      conclusion: 'Standardized fermentation procedures elevate Bohol bean market value by up to 35% over unfermented beans.'
    }
  },
  {
    id: 'res-default-4',
    title: 'Economic Feasibility and Value-Chain Analysis of Smallholder Cacao Farmers in Visayas',
    authors: 'Dr. Jonathan Cruz, Ana Patricia Ramos',
    institution: 'Central Visayas Economic Research Institute',
    category: 'Market & Economy',
    date: '2025-03-05',
    fileName: 'Visayas_Smallholder_Cacao_Economics_2025.docx',
    fileSize: '1.4 MB',
    fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    downloads: 167,
    description:
      'Economic assessment examining cost of inputs, labor requirements, cooperative direct selling channels, and profit margins for cacao farms ranging from 0.5 to 5 hectares.',
    sections: {
      abstract: 'Financial feasibility study quantifying net farm income, break-even price points, and cooperative marketing leverage for Visayan cacao growers.',
      introduction: 'Understanding the financial dynamics of smallholder farming is key to scaling sustainable agricultural development projects in Bohol.',
      methodology: 'Survey of 120 cacao farmers across 8 agricultural cooperatives, analyzing capital expenditure, operating costs, and price realization rates.',
      keyFindings: [
        'Direct cooperative sales reduced middleman margin losses by 18%.',
        'Intercropping cacao with coconut trees increased net income per hectare by PhP 45,000 annually.',
        'Break-even cost per kilogram of dried fermented beans was calculated at PhP 115.'
      ],
      conclusion: 'Cacao farming combined with intercropping offers robust long-term financial returns and economic stability for rural households.'
    }
  }
];

// Helper to convert DOCX ArrayBuffer to clean HTML using mammoth or XML fallback
const convertDocxToHtml = async (arrayBuffer) => {
  try {
    let mammothLib = window.mammoth;
    if (!mammothLib) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load mammoth parser script'));
        document.head.appendChild(script);
      });
      mammothLib = window.mammoth;
    }

    if (mammothLib && mammothLib.convertToHtml) {
      const res = await mammothLib.convertToHtml({ arrayBuffer });
      if (res && res.value && res.value.trim().length > 0) {
        return res.value;
      }
    }
  } catch (e) {
    console.warn('Mammoth DOCX parsing fallback triggered:', e);
  }

  // Fallback XML paragraph text extractor
  try {
    const decoder = new TextDecoder('utf-8');
    const text = decoder.decode(arrayBuffer);
    const matches = text.match(/<w:t[^>]*>(.*?)<\/w:t>/g);
    if (matches && matches.length > 0) {
      const cleanText = matches
        .map((m) => m.replace(/<[^>]+>/g, '').trim())
        .filter((t) => t.length > 0)
        .join(' ');
      return `<p className="leading-relaxed text-gray-800">${cleanText}</p>`;
    }
  } catch (e) {
    // Ignore fallback errors
  }

  return '';
};

export function Research({ user }) {
  const isAdmin = user?.role === 'admin';
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // Modals
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null); // for details / preview modal
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'details'
  const [zoomLevel, setZoomLevel] = useState(100);
  const [copied, setCopied] = useState(false);
  const [deletePaperId, setDeletePaperId] = useState(null);

  // Real DOCX parsed content state
  const [docxPreviewHtml, setDocxPreviewHtml] = useState('');
  const [docxLoading, setDocxLoading] = useState(false);

  // Form State
  const [form, setForm] = useState({
    title: '',
    authors: '',
    institution: '',
    category: 'Farming & Agronomy',
    date: new Date().toISOString().split('T')[0],
    description: '',
    file: null,
    fileName: '',
    fileSize: '',
    fileType: '',
    fileDataUrl: '',
    fileHtml: '',
  });

  const { message, showSuccess, showError, dismiss } = useToast();

  // Parse DOCX content whenever a DOCX paper is selected for viewing
  useEffect(() => {
    if (!selectedPaper) {
      setDocxPreviewHtml('');
      setDocxLoading(false);
      return;
    }

    const isDocx = selectedPaper.fileName?.endsWith('.docx') || selectedPaper.fileType?.includes('word');

    if (isDocx) {
      if (selectedPaper.fileHtml) {
        setDocxPreviewHtml(selectedPaper.fileHtml);
      } else if (selectedPaper.fileDataUrl && selectedPaper.fileDataUrl.startsWith('data:')) {
        setDocxLoading(true);
        try {
          const parts = selectedPaper.fileDataUrl.split(',');
          const base64Str = parts[1];
          const binaryStr = window.atob(base64Str);
          const len = binaryStr.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
          }
          convertDocxToHtml(bytes.buffer)
            .then((html) => {
              setDocxPreviewHtml(html);
              setDocxLoading(false);
            })
            .catch(() => {
              setDocxLoading(false);
            });
        } catch (err) {
          setDocxLoading(false);
        }
      } else {
        setDocxPreviewHtml('');
      }
    }
  }, [selectedPaper]);

  // Load from backend API or local storage / default papers
  useEffect(() => {
    const loadPapers = async () => {
      setLoading(true);
      try {
        if (researchAPI && researchAPI.getAll) {
          const res = await researchAPI.getAll();
          const serverData = res?.data || (Array.isArray(res) ? res : []);
          if (serverData && serverData.length > 0) {
            setPapers(serverData);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(serverData));
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        // Backend offline, fallback to local storage
      }

      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setPapers(parsed);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        // Ignore parse error
      }

      // Default papers fallback
      setPapers(DEFAULT_PAPERS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_PAPERS));
      setLoading(false);
    };

    loadPapers();
  }, []);

  // Save changes to localStorage helper
  const savePapersLocally = (updated) => {
    setPapers(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      // Ignore quota error if base64 files get large
    }
  };

  // Filter papers
  const filteredPapers = useMemo(() => {
    return papers.filter((paper) => {
      const matchesCat = selectedCategory === 'All' || paper.category === selectedCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchesQuery =
        !q ||
        paper.title.toLowerCase().includes(q) ||
        paper.authors.toLowerCase().includes(q) ||
        paper.institution.toLowerCase().includes(q) ||
        paper.description.toLowerCase().includes(q);
      return matchesCat && matchesQuery;
    });
  }, [papers, selectedCategory, searchQuery]);

  // File Upload Handler — PDF only
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Strict PDF-only check by MIME type and file extension
    const isPdf =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      showError('Only PDF files (.pdf) are accepted. Please upload a valid PDF document.');
      // Reset the input so the user can try again
      e.target.value = '';
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      showError('File size exceeds 15MB limit.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        file: file,
        fileName: file.name,
        fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
        fileType: 'application/pdf',
        fileDataUrl: reader.result,
        fileHtml: null,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Submit Upload Form
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.authors.trim() || !form.description.trim()) {
      showError('Please fill in all required fields (Title, Authors, Description).');
      return;
    }

    const paperPayload = {
      title: form.title.trim(),
      authors: form.authors.trim(),
      institution: form.institution.trim() || 'BISU Bilar Research Center',
      category: form.category,
      date: form.date || new Date().toISOString().split('T')[0],
      description: form.description.trim(),
      fileName: form.fileName || (form.title.trim().toLowerCase().replace(/[^a-z0-9]/g, '_') + '.pdf'),
      fileSize: form.fileSize || '1.5 MB',
      fileType: form.fileType || 'application/pdf',
      fileDataUrl: form.fileDataUrl || null,
      fileHtml: form.fileHtml || null,
    };

    let createdId = 'res-' + Date.now();

    try {
      if (researchAPI && researchAPI.create) {
        const res = await researchAPI.create(paperPayload);
        if (res?.research_id) {
          createdId = res.research_id;
        }
      }
    } catch (e) {
      // Saved locally if server fails
    }

    const newPaper = { id: createdId, ...paperPayload, downloads: 0 };
    const updated = [newPaper, ...papers];
    savePapersLocally(updated);
    showSuccess('Research paper uploaded successfully!');
    
    // Reset form & close modal
    setForm({
      title: '',
      authors: '',
      institution: '',
      category: 'Farming & Agronomy',
      date: new Date().toISOString().split('T')[0],
      description: '',
      file: null,
      fileName: '',
      fileSize: '',
      fileType: '',
      fileDataUrl: '',
      fileHtml: '',
    });
    setIsUploadOpen(false);
  };

  // Delete Paper
  const handleDelete = async (id) => {
    try {
      if (researchAPI && researchAPI.delete) {
        await researchAPI.delete(id);
      }
    } catch (e) {
      // Server delete attempt completed
    }

    const updated = papers.filter((p) => (p.id || p.research_id) !== id);
    savePapersLocally(updated);
    showSuccess('Research paper removed.');
    if (selectedPaper && (selectedPaper.id === id || selectedPaper.research_id === id)) {
      setSelectedPaper(null);
    }
    setDeletePaperId(null);
  };

  // Trigger Download & Increment Counter
  const handleDownload = async (paper) => {
    const id = paper.id || paper.research_id;
    try {
      if (researchAPI && researchAPI.incrementDownload) {
        await researchAPI.incrementDownload(id);
      }
    } catch (e) {
      // Increment local count if server offline
    }

    const updated = papers.map((p) => ((p.id || p.research_id) === id ? { ...p, downloads: (p.downloads || 0) + 1 } : p));
    savePapersLocally(updated);

    if (paper.fileDataUrl) {
      const a = document.createElement('a');
      a.href = paper.fileDataUrl;
      a.download = paper.fileName || `${paper.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      const isDocx = paper.fileName?.endsWith('.docx') || paper.fileType?.includes('word');
      const ext = isDocx ? 'docx' : 'pdf';
      const content = `========================================================================\nRESEARCH PAPER DOCUMENT\n========================================================================\n\nTITLE: ${paper.title}\nAUTHORS: ${paper.authors}\nINSTITUTION: ${paper.institution}\nPUBLISHED DATE: ${paper.date}\nCATEGORY: ${paper.category}\n\nABSTRACT:\n${paper.description}\n\n${paper.sections?.introduction ? `1. INTRODUCTION\n${paper.sections.introduction}\n\n2. METHODOLOGY\n${paper.sections.methodology}\n\n3. KEY FINDINGS\n${paper.sections.keyFindings.map((f, i) => ` - ${f}`).join('\n')}\n\n4. CONCLUSION\n${paper.sections.conclusion}\n` : ''}\n========================================================================\nBohol Cacao Industry Information System (BCIIS) Research Portal\n========================================================================`;
      
      const blob = new Blob([content], {
        type: isDocx ? 'application/msword;charset=utf-8;' : 'text/plain;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = paper.fileName || `${paper.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Open Preview Modal helper
  const handleOpenPreview = (paper, tab = 'preview') => {
    setSelectedPaper(paper);
    setActiveTab(tab);
    setZoomLevel(100);
    setCopied(false);
  };

  // Copy share link helper
  const handleCopyLink = (paper) => {
    navigator.clipboard.writeText(`${window.location.origin}/research?id=${paper.id || paper.research_id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/60 via-stone-50 to-amber-100/30 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <Toast message={message} onDismiss={dismiss} />

        {/* ── Banner / Hero Section ───────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-950 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
            <BookOpen className="w-80 h-80 text-amber-200" />
          </div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-2 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-700/60 border border-amber-500/40 text-amber-200 text-xs font-semibold uppercase tracking-wider">
                <BookOpen className="w-3.5 h-3.5" />
                Knowledge Base & Academic Portal
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                Cacao Industry Research & Publications
              </h1>
              <p className="text-amber-100/90 text-sm sm:text-base leading-relaxed">
                Access, upload, and explore scientific studies, technical manuals, and market reports focused on advancing cacao agriculture and processing in Bohol.
              </p>
            </div>

            {isAdmin && (
              <button
                onClick={() => setIsUploadOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-amber-500 hover:bg-amber-400 text-amber-950 font-bold text-sm rounded-2xl shadow-lg hover:shadow-amber-500/30 transition-all transform hover:scale-105 active:scale-95 shrink-0"
              >
                <Upload className="w-5 h-5" />
                <span>Upload Research</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Search & Category Filter Bar ───────────────────────────────── */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-md border border-amber-100 space-y-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search papers by title, author, institution, keywords..."
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm outline-none transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Total Count */}
            <div className="text-xs font-semibold text-amber-800 bg-amber-50 px-4 py-3 rounded-xl border border-amber-200/60 shrink-0">
              Showing {filteredPapers.length} of {papers.length} Papers
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-1 scrollbar-none">
            <Filter className="w-4 h-4 text-amber-700 shrink-0 mr-1" />
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                  selectedCategory === cat
                    ? 'bg-amber-800 text-white shadow-md'
                    : 'bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-900'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* ── Research Cards Grid ────────────────────────────────────────── */}
        {loading ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-stone-200 shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-700 mx-auto mb-3"></div>
            <p className="text-sm font-medium text-stone-600">Loading research publications...</p>
          </div>
        ) : filteredPapers.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-dashed border-gray-300 shadow-sm space-y-3">
            <FileText className="w-16 h-16 text-amber-300 mx-auto" />
            <h3 className="text-lg font-bold text-gray-800">No Research Papers Found</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              We couldn't find any papers matching your search parameters. Try adjusting your search query or upload a new research document.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
              }}
              className="mt-2 text-xs font-bold text-amber-800 underline hover:text-amber-900"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {filteredPapers.map((paper) => {
              const isPdf = paper.fileName?.endsWith('.pdf') || paper.fileType?.includes('pdf');
              return (
                <div
                  key={paper.id || paper.research_id}
                  className="bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-xl hover:border-amber-300 transition-all duration-300 flex flex-col justify-between overflow-hidden group"
                >
                  {/* Card Content */}
                  <div className="p-6 space-y-4">
                    {/* Top Row: Category & File Format Badge */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-200">
                        {paper.category}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-bold ${
                        isPdf ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        <FileText className="w-3.5 h-3.5" />
                        {isPdf ? 'PDF' : 'DOCX'}
                      </span>
                    </div>

                    {/* Title */}
                    <h3
                      onClick={() => handleOpenPreview(paper, 'preview')}
                      className="text-base font-bold text-gray-900 group-hover:text-amber-800 transition-colors cursor-pointer line-clamp-2 leading-snug"
                    >
                      {paper.title}
                    </h3>

                    {/* Authors & Institution */}
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-2 text-gray-700 font-medium truncate">
                        <User className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                        <span className="truncate">{paper.authors}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500 truncate">
                        <Building className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                        <span className="truncate">{paper.institution}</span>
                      </div>
                    </div>

                    {/* Abstract preview */}
                    <p className="text-xs text-gray-600 line-clamp-3 leading-relaxed">
                      {paper.description}
                    </p>
                  </div>

                  {/* Card Footer Actions */}
                  <div className="bg-stone-50 px-6 py-3.5 border-t border-stone-100 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span>{paper.date}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* View Button - Opens Paper Preview Modal */}
                      <button
                        onClick={() => handleOpenPreview(paper, 'preview')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-amber-950 bg-amber-200/80 hover:bg-amber-300 transition-colors shadow-sm"
                        title="View Paper & File Preview"
                      >
                        <Eye className="w-3.5 h-3.5 text-amber-900" />
                        <span>View</span>
                      </button>

                      {/* Download Button */}
                      <button
                        onClick={() => handleDownload(paper)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-amber-800 hover:bg-amber-900 transition-colors shadow-sm"
                        title="Download Research Document"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Download</span>
                      </button>

                      {/* Delete Button — Admin only */}
                      {isAdmin && (
                        <button
                          onClick={() => setDeletePaperId(paper.id || paper.research_id)}
                          className="p-1.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete Research"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* ── UPLOAD RESEARCH MODAL — Admin only ───────────────────────────── */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      {isAdmin && isUploadOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setIsUploadOpen(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slide-down border border-amber-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-900 to-amber-800 px-6 py-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-700/60">
                  <Upload className="w-5 h-5 text-amber-200" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Upload Research Paper</h2>
                  <p className="text-xs text-amber-200/80">PDF files only (.pdf)</p>
                </div>
              </div>
              <button
                onClick={() => setIsUploadOpen(false)}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleUploadSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Research Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Soil Micronutrient Analysis in Bilar Cacao Farms"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm outline-none transition-all"
                />
              </div>

              {/* Authors & Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Authors / Researchers <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.authors}
                    onChange={(e) => setForm({ ...form, authors: e.target.value })}
                    placeholder="e.g. Dr. Maria Santos, Juan Dela Cruz"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm outline-none transition-all bg-white"
                  >
                    {CATEGORIES.filter((c) => c !== 'All').map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Institution & Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Institution / Agency
                  </label>
                  <input
                    type="text"
                    value={form.institution}
                    onChange={(e) => setForm({ ...form, institution: e.target.value })}
                    placeholder="e.g. BISU Bilar, DOST, DA"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Date Conducted / Published
                  </label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm outline-none transition-all bg-white"
                  />
                </div>
              </div>

              {/* Description / Abstract */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Abstract / Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Provide a short summary of research findings, objectives, and conclusions..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm outline-none transition-all resize-none"
                />
              </div>

              {/* Drag & Drop File Upload Area */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Attach Research File (PDF ONLY)
                </label>
                <div className="mt-1 border-2 border-dashed border-amber-300 hover:border-amber-500 rounded-2xl p-4 text-center bg-amber-50/40 hover:bg-amber-50 transition-colors relative cursor-pointer group">
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleFileChange}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  />
                  <div className="flex flex-col items-center justify-center space-y-1">
                    <FileText className="w-8 h-8 text-amber-700 group-hover:scale-110 transition-transform" />
                    {form.fileName ? (
                      <div className="text-xs font-bold text-amber-900 bg-amber-100 px-3 py-1 rounded-full border border-amber-200">
                        {form.fileName} ({form.fileSize})
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-bold text-amber-900">
                          Click or drag file here to attach
                        </p>
                        <p className="text-[11px] text-gray-500">PDF files only (.pdf) • Max 15MB</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsUploadOpen(false)}
                  className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-bold text-xs hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl bg-amber-800 hover:bg-amber-900 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Upload Paper</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* ── RESEARCH PAPER PREVIEW MODAL ─────────────────────────────────── */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      {selectedPaper && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(5px)' }}
          onClick={() => setSelectedPaper(null)}
        >
          <div
            className="bg-stone-900 rounded-3xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden border border-amber-500/30 animate-slide-down"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Modal Header Banner ────────────────────────────────────── */}
            <div className="bg-gradient-to-r from-amber-950 via-amber-900 to-stone-900 px-6 py-4 border-b border-amber-800/40 flex items-center justify-between text-white shrink-0">
              <div className="flex items-center gap-3 truncate pr-4">
                <div className="p-2.5 rounded-2xl bg-amber-600/30 border border-amber-500/40 text-amber-300 shrink-0">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="truncate">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400 bg-amber-950 px-2 py-0.5 rounded border border-amber-800/60">
                      {selectedPaper.category}
                    </span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                      selectedPaper.fileName?.endsWith('.docx') || selectedPaper.fileType?.includes('word')
                        ? 'bg-blue-900/80 text-blue-200 border border-blue-700/60'
                        : 'bg-red-900/80 text-red-200 border border-red-700/60'
                    }`}>
                      {selectedPaper.fileName?.endsWith('.docx') || selectedPaper.fileType?.includes('word') ? 'DOCX Document' : 'PDF Document'}
                    </span>
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-stone-100 truncate mt-0.5">
                    {selectedPaper.title}
                  </h2>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setSelectedPaper(null)}
                className="p-2 rounded-2xl bg-stone-800/80 hover:bg-stone-700 text-stone-300 hover:text-white transition-colors border border-stone-700 shrink-0"
                title="Close Preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* ── Modal Navigation & Toolbar Bar ─────────────────────────── */}
            <div className="bg-stone-800/90 px-6 py-2.5 border-b border-stone-700 flex flex-wrap items-center justify-between gap-3 text-xs shrink-0">
              {/* Tab Switcher */}
              <div className="flex items-center bg-stone-950 p-1 rounded-xl border border-stone-700">
                <button
                  onClick={() => setActiveTab('preview')}
                  className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-bold transition-all ${
                    activeTab === 'preview'
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'text-stone-400 hover:text-stone-200'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Document Preview</span>
                </button>
                <button
                  onClick={() => setActiveTab('details')}
                  className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-bold transition-all ${
                    activeTab === 'details'
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'text-stone-400 hover:text-stone-200'
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>Summary & Details</span>
                </button>
              </div>

              {/* Toolbar Controls (Zoom, Share, Download) */}
              <div className="flex items-center gap-2 text-stone-300">
                {activeTab === 'preview' && (
                  <div className="flex items-center bg-stone-900 border border-stone-700 rounded-xl px-2 py-1 gap-2">
                    <button
                      onClick={() => setZoomLevel((prev) => Math.max(75, prev - 15))}
                      className="p-1 hover:text-white hover:bg-stone-800 rounded transition-colors"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-mono text-[11px] text-amber-300 w-10 text-center font-bold">
                      {zoomLevel}%
                    </span>
                    <button
                      onClick={() => setZoomLevel((prev) => Math.min(150, prev + 15))}
                      className="p-1 hover:text-white hover:bg-stone-800 rounded transition-colors"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                <button
                  onClick={() => handleCopyLink(selectedPaper)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-900 hover:bg-stone-700 border border-stone-700 text-stone-200 rounded-xl font-bold transition-colors"
                  title="Copy Document Link"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Share2 className="w-3.5 h-3.5" />}
                  <span>{copied ? 'Copied' : 'Share'}</span>
                </button>

                <button
                  onClick={() => handleDownload(selectedPaper)}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-bold transition-colors shadow-md"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download</span>
                </button>
              </div>
            </div>

            {/* ── Modal Content Area ─────────────────────────────────────── */}
            <div className="flex-1 bg-stone-950 overflow-y-auto p-4 sm:p-8 flex justify-center">
              {activeTab === 'preview' ? (
                /* ── TAB 1: DOCUMENT PREVIEW (PDF OR DOCX) ─────────────── */
                <div
                  className="w-full max-w-4xl transition-transform duration-200 origin-top"
                  style={{ transform: `scale(${zoomLevel / 100})` }}
                >
                  {selectedPaper.fileDataUrl && selectedPaper.fileType?.includes('pdf') ? (
                    /* Embedded PDF Base64 Viewer */
                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-stone-700 h-[700px]">
                      <iframe
                        src={selectedPaper.fileDataUrl}
                        title={selectedPaper.title}
                        className="w-full h-full border-none"
                      />
                    </div>
                  ) : selectedPaper.fileName?.endsWith('.docx') || selectedPaper.fileType?.includes('word') ? (
                    /* ── DOCX DOCUMENT READER VIEW ────────────────────────── */
                    <div className="bg-white rounded-2xl shadow-2xl border border-stone-300 text-stone-900 overflow-hidden font-sans">
                      {/* Word Header Ribbon Simulation */}
                      <div className="bg-blue-800 text-white px-6 py-3 flex items-center justify-between text-xs font-bold border-b border-blue-900">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-200" />
                          <span>Microsoft Word Document Viewer — {selectedPaper.fileName}</span>
                        </div>
                        <span className="bg-blue-900/60 px-2 py-0.5 rounded text-[11px] text-blue-200">
                          {selectedPaper.fileSize || 'DOCX File'}
                        </span>
                      </div>

                      {/* Word Page Container */}
                      <div className="p-8 sm:p-14 space-y-8 bg-white min-h-[750px]">
                        {/* Document Title Header */}
                        <div className="border-b border-stone-200 pb-6 space-y-3">
                          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 leading-tight">
                            {selectedPaper.title}
                          </h1>
                          <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-gray-600 font-medium">
                            <span className="text-amber-900 font-bold">Authors: {selectedPaper.authors}</span>
                            <span>•</span>
                            <span>{selectedPaper.institution}</span>
                            <span>•</span>
                            <span>Date: {selectedPaper.date}</span>
                          </div>
                        </div>

                        {/* Executive Abstract Box */}
                        <div className="bg-blue-50/70 border-l-4 border-blue-600 p-5 rounded-r-2xl space-y-2">
                          <h3 className="text-xs font-extrabold text-blue-900 uppercase tracking-wider">
                            Executive Abstract
                          </h3>
                          <p className="text-sm text-blue-950 leading-relaxed italic">
                            "{selectedPaper.description}"
                          </p>
                        </div>

                        {/* Real Uploaded DOCX Content Render or Structured Sections */}
                        {docxLoading ? (
                          <div className="p-12 text-center space-y-3 bg-stone-50 rounded-2xl border border-stone-200">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mx-auto"></div>
                            <p className="text-xs font-bold text-gray-700">Reading uploaded Word (.docx) document content...</p>
                          </div>
                        ) : docxPreviewHtml ? (
                          <div className="space-y-4">
                            <h3 className="text-xs font-extrabold text-blue-900 uppercase tracking-wider border-b border-stone-200 pb-2">
                              Uploaded Document Full Text & Content
                            </h3>
                            <div
                              className="prose prose-sm max-w-none text-gray-800 leading-relaxed space-y-3 
                                [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mt-6 [&_h1]:mb-2 [&_h1]:border-b [&_h1]:pb-1
                                [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-5 [&_h2]:mb-2
                                [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-4 [&_h3]:mb-1
                                [&_p]:text-sm [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-3
                                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:mb-3
                                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_ol]:mb-3
                                [&_table]:w-full [&_table]:border-collapse [&_table]:border [&_table]:border-stone-300 [&_table]:my-4 [&_table]:text-xs
                                [&_th]:border [&_th]:border-stone-300 [&_th]:bg-stone-100 [&_th]:p-2 [&_th]:font-bold
                                [&_td]:border [&_td]:border-stone-300 [&_td]:p-2
                                [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-xl [&_img]:my-3"
                              dangerouslySetInnerHTML={{ __html: docxPreviewHtml }}
                            />
                          </div>
                        ) : selectedPaper.sections ? (
                          <div className="space-y-6 text-sm text-gray-800 leading-relaxed">
                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-gray-900 border-b border-stone-200 pb-1">
                                1. Introduction & Context
                              </h3>
                              <p>{selectedPaper.sections.introduction}</p>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-gray-900 border-b border-stone-200 pb-1">
                                2. Research Methodology
                              </h3>
                              <p>{selectedPaper.sections.methodology}</p>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-gray-900 border-b border-stone-200 pb-1">
                                3. Key Findings & Empirical Data
                              </h3>
                              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                                {selectedPaper.sections.keyFindings.map((finding, idx) => (
                                  <li key={idx} className="leading-snug">
                                    {finding}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-gray-900 border-b border-stone-200 pb-1">
                                4. Conclusion & Operational Recommendations
                              </h3>
                              <p>{selectedPaper.sections.conclusion}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4 text-sm text-gray-800 leading-relaxed">
                            <h3 className="text-base font-bold text-gray-900 border-b border-stone-200 pb-1">
                              Document Overview & Content
                            </h3>
                            <p>{selectedPaper.description}</p>
                          </div>
                        )}

                        {/* Document Footer */}
                        <div className="pt-8 border-t border-stone-200 flex items-center justify-between text-xs text-stone-500 font-mono">
                          <span>Bohol Cacao Industry Information System (BCIIS)</span>
                          <span>Page 1 of 1</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* ── PDF DOCUMENT READER VIEW ────────────────────────── */
                    <div className="bg-white rounded-2xl shadow-2xl border border-stone-300 text-stone-900 overflow-hidden font-serif">
                      {/* PDF Toolbar Header Simulation */}
                      <div className="bg-red-800 text-white px-6 py-3 flex items-center justify-between text-xs font-bold border-b border-red-900 font-sans">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-red-200" />
                          <span>PDF Reader — {selectedPaper.fileName}</span>
                        </div>
                        <span className="bg-red-900/60 px-2 py-0.5 rounded text-[11px] text-red-200">
                          {selectedPaper.fileSize || 'PDF Document'}
                        </span>
                      </div>

                      {/* PDF Page Container */}
                      <div className="p-8 sm:p-14 space-y-8 bg-white min-h-[750px]">
                        {/* Academic Header Banner */}
                        <div className="text-center space-y-4 border-b-2 border-amber-900/20 pb-8">
                          <div className="inline-block px-3 py-1 bg-amber-100 text-amber-900 rounded-full text-xs font-sans font-bold uppercase tracking-wider">
                            {selectedPaper.category} • Academic Research Publication
                          </div>
                          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight leading-tight">
                            {selectedPaper.title}
                          </h1>
                          <div className="text-xs font-sans text-stone-600 space-y-1">
                            <p className="font-bold text-stone-800">{selectedPaper.authors}</p>
                            <p className="italic">{selectedPaper.institution}</p>
                            <p className="text-stone-500">Published: {selectedPaper.date}</p>
                          </div>
                        </div>

                        {/* Abstract Box */}
                        <div className="bg-amber-50/60 border border-amber-200 p-6 rounded-2xl space-y-2 font-sans">
                          <h3 className="text-xs font-extrabold text-amber-900 uppercase tracking-wider">
                            ABSTRACT
                          </h3>
                          <p className="text-sm text-stone-800 leading-relaxed">
                            {selectedPaper.description}
                          </p>
                        </div>

                        {/* PDF Sections */}
                        {selectedPaper.sections ? (
                          <div className="space-y-6 text-sm text-stone-800 leading-relaxed font-sans">
                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-amber-950 border-b border-stone-200 pb-1">
                                1. Introduction
                              </h3>
                              <p>{selectedPaper.sections.introduction}</p>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-amber-950 border-b border-stone-200 pb-1">
                                2. Materials & Experimental Methodology
                              </h3>
                              <p>{selectedPaper.sections.methodology}</p>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-amber-950 border-b border-stone-200 pb-1">
                                3. Results & Discussion
                              </h3>
                              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200 space-y-2">
                                {selectedPaper.sections.keyFindings.map((finding, idx) => (
                                  <div key={idx} className="flex items-start gap-2 text-stone-700">
                                    <span className="text-amber-800 font-bold">•</span>
                                    <span>{finding}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-base font-bold text-amber-950 border-b border-stone-200 pb-1">
                                4. Conclusions & Policy Implications
                              </h3>
                              <p>{selectedPaper.sections.conclusion}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4 text-sm text-stone-800 leading-relaxed font-sans">
                            <h3 className="text-base font-bold text-amber-950 border-b border-stone-200 pb-1">
                              Publication Contents
                            </h3>
                            <p>{selectedPaper.description}</p>
                          </div>
                        )}

                        {/* Page Divider / Page Number */}
                        <div className="pt-8 border-t border-stone-200 flex items-center justify-between text-xs text-stone-400 font-sans">
                          <span>BCIIS Research Repository • Bohol</span>
                          <span>Page 1 of 1</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* ── TAB 2: SUMMARY & METADATA DETAILS ─────────────────── */
                <div className="w-full max-w-3xl bg-stone-900 border border-stone-800 rounded-3xl p-6 sm:p-8 text-stone-200 space-y-6">
                  {/* Title & Category */}
                  <div className="space-y-2">
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      {selectedPaper.category}
                    </span>
                    <h2 className="text-xl sm:text-2xl font-bold text-white leading-snug">
                      {selectedPaper.title}
                    </h2>
                  </div>

                  {/* Metadata Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 rounded-2xl bg-stone-950 border border-stone-800 text-xs">
                    <div>
                      <span className="text-amber-400 font-bold block mb-1">Authors / Researchers</span>
                      <span className="text-stone-300 text-sm font-medium">{selectedPaper.authors}</span>
                    </div>

                    <div>
                      <span className="text-amber-400 font-bold block mb-1">Affiliated Institution</span>
                      <span className="text-stone-300 text-sm font-medium">{selectedPaper.institution}</span>
                    </div>

                    <div>
                      <span className="text-amber-400 font-bold block mb-1">Date Published / Conducted</span>
                      <span className="text-stone-300 text-sm font-medium">{selectedPaper.date}</span>
                    </div>

                    <div>
                      <span className="text-amber-400 font-bold block mb-1">Total Downloads</span>
                      <span className="text-stone-300 text-sm font-medium">{selectedPaper.downloads || 0} Downloads</span>
                    </div>
                  </div>

                  {/* Abstract Section */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-amber-400">
                      Abstract & Study Overview
                    </h3>
                    <div className="bg-stone-950 p-5 rounded-2xl border border-stone-800 text-sm text-stone-300 leading-relaxed">
                      {selectedPaper.description}
                    </div>
                  </div>

                  {/* Attached File Details Card */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-amber-400">
                      Attached Document Metadata
                    </h3>
                    <div className="flex items-center justify-between p-4 rounded-2xl bg-stone-950 border border-stone-800">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-xl bg-amber-900/50 text-amber-400 border border-amber-700/50">
                          <FileText className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white">{selectedPaper.fileName}</p>
                          <p className="text-xs text-stone-400">
                            Size: {selectedPaper.fileSize || '1.5 MB'} • Format: {selectedPaper.fileName?.endsWith('.docx') ? 'Word (.docx)' : 'PDF (.pdf)'}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => handleDownload(selectedPaper)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold shadow-md transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Modal Footer Bar ───────────────────────────────────────── */}
            <div className="bg-stone-900 px-6 py-4 border-t border-stone-800 flex items-center justify-between shrink-0 text-xs">
              <span className="text-stone-400 font-medium hidden sm:inline">
                {selectedPaper.downloads || 0} downloads recorded • Bohol Cacao Research Portal
              </span>

              <div className="flex items-center gap-3 ml-auto">
                <button
                  onClick={() => setSelectedPaper(null)}
                  className="px-5 py-2.5 rounded-xl border border-stone-700 text-stone-300 hover:text-white font-bold hover:bg-stone-800 transition-colors"
                >
                  Close Preview
                </button>
                <button
                  onClick={() => handleDownload(selectedPaper)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold transition-all shadow-md"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Document</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRMATION MODAL ───────────────────────────────────── */}
      {deletePaperId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setDeletePaperId(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900">Delete Research Paper?</h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              Are you sure you want to remove this research document from the database? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setDeletePaperId(null)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deletePaperId)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 transition-colors shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Research;

